/**
 * 本文中の用語を自動リンクして描く（大枠設計 §7 の表示パターン #4「用語解決」）。
 *
 * 当てる材料は**画面が 1 回だけ取る用語辞書**である。応答ごとに用語解決を同梱する形は、
 * 本文を持つ応答すべてに位置つきの解決を積む必要があり、実際には総合テストの説明文 1 箇所に
 * しか積まれていなかった。一覧の行やタイトルには積みようが無い（行ごとに用語解決を走らせる
 * ことになる）。辞書を context で配れば、本文がどこにあっても同じ規則で当たる。
 *
 * リンク先は用語詳細ただ 1 つなので、`fallback-states` の復帰導線とは違って画面ごとに
 * 変わらない。行き先が 1 つしか無い関係をスロットで受け取ると、画面ごとに同じルート定数を
 * 書き写すことになり、綴り違いが混ざり込む。したがってここはルートを直接知る。
 *
 * 定義は `title` 属性で出す。独自のポップオーバーを作ると、本文の途中に focus 可能な要素と
 * 開閉状態が増えて、読み物としての本文がキーボード操作の迷路になる。`title` なら hover と
 * キーボード focus の両方でブラウザ標準の出し方に乗る。
 *
 * ## ここを通さない場所（2026-09-14 合意）
 *
 * **一覧 7 画面の行**（画面 / 業務 / 用語 / 総合テスト / アクター / ドメイン / 業務ツリー）。
 * 行はカード全体が詳細への `<Link>` で、その中に用語リンクを置くと `<a>` の入れ子になり
 * HTML として成立しない。行リンクをやめれば置けるが、一覧で用語リンクが付く 480 箇所のうち
 * 397 箇所は「名前」で、名前は行を押して開いた詳細の見出しに同じものが出てそこには既に
 * リンクが付く。一手間増えるだけで引けない語は生まれないため、316 行ぶんの「どこを押しても
 * 開く」を手放す側が高くつく。**一覧では用語が引けなくてよい**（利用者の判断）。
 *
 * **フロー図の節点**。節点を押すと右の詳細ペインが開くのが図の主操作で、用語リンクを置くと
 * その操作を奪う。詳細ペイン側は既にここを通っているので、一手間で同じ語へ辿れる。
 *
 * どちらも「付け忘れ」ではない。付けられる場所を増やすときは、リンクの入れ子と操作の
 * 奪い合いが起きないことを先に確かめる。
 *
 * ## 番号つきの参照（2026-09-29 合意「用語に一致するものはリンクにする」）
 *
 * 業務の入力・業務ルールの出力・判定表のセルのように、**番号つきで用語を指している参照**は
 * `ElementTermLink` で描く。本文と違って表示名を当てる必要が無く、番号が用語集に居れば用語である。
 * 参照を描く共有部品（`ElementRefText` と部品名）がこれを通るので、それを使う詳細画面・
 * 右パネルへ一度に効く。
 *
 * ## 辞書の出どころ（作り直しの用語表）
 *
 * **辞書は `listTerms`（作り直しの要素表）から組む。** 用語詳細のルートが受けるのは
 * 番号なので、番号で引ける表から組んで、押した先が開くようにする。綴りの ID（`term/item`）で
 * リンクを作ると、本文の語は `/terms/item` へ繋がり**必ず「見つかりません」になる**。
 *
 * **同じ表示名の用語が 2 件以上あればリンクを作らない。** 番号は表示名から引くしかないので、
 * 同名が複数あるとどちらへ送るかを決められない。片方へ送れば半分は嘘の行き先になる。リンクに
 * しなければ、語は読めるままで誤誘導だけが消える。
 *
 * **当てるのは表示言語の正表記だけである。** 旧の辞書は言語をまたぐ全表記と揺れ表記を配って
 * いたが、作り直しの用語集が返すのは表示言語へ畳んだ表示名 1 つである。別言語で書かれた箇所と
 * 揺れ表記は当たらない。表記の索引が作り直しの契約へ載るまではこの範囲で当てる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 本文中の用語を、定義を添えた用語詳細へのリンクとして描く。
 * @graph-connects TERM_DETAIL_ROUTE [references] 用語詳細
 * @graph-connects HighlightedText [calls] PJ 差分で変わった語の強調
 * @graph-connects useListTerms [calls] 全画面で共有する作り直しの用語集の取得
 * @graph-connects pjLinkSearch [calls] 案件を次の画面へ引き継ぐ検索条件
 */
import type { TermSummary } from '@cortex/domain-spec-console';
import { Link } from '@tanstack/react-router';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { toElementId, useListTerms } from '../../hooks/queries/spec-graph-element-term-queries.js';
import { toTermSegments, type TermLinkEntry } from '../../lib/term-segments.js';
import { pjLinkSearch, type PjLinkSearch } from '../../routes/pj-link.js';
import { TERM_DETAIL_ROUTE } from '../../routes/route-paths.js';
import { HighlightedText, type WordHighlight } from './DiffText.js';

/**
 * 用語リンクが要る材料。辞書・リンクを作らない用語 1 件・次の画面へ引き継ぐ案件。
 * @graph-connects none
 */
interface TermLinkContextValue {
  terms: readonly TermLinkEntry[];
  /**
   * 用語集に居る用語の番号すべて。参照（番号つき）が用語を指しているかを引くのに使う。
   *
   * `terms` と別に持つのは、**番号で引くなら同じ表示名の用語も区別できる**ためである。`terms` は
   * 表示名で本文に当てる辞書なので同名を落とすが、参照は番号を持っているので落とす理由が無い。
   */
  termIds: ReadonlySet<string>;
  suppressedTermId: string | null;
  /**
   * 用語詳細へのリンクに載せる検索条件。案件を選んでいなければ空。
   *
   * **本文のリンクも案件を引き継ぐ。** 落とすと、案件を適用した姿を読んでいる利用者が押した
   * 先だけマスターへ落ちる。案件が新設した用語なら、そこは「見つかりません」になる。
   */
  pjSearch: PjLinkSearch;
}

/**
 * 辞書が無い状態の既定値。
 *
 * 空の辞書は「まだ届いていない」も「用語が 1 件も無い」も同じ形で表す。どちらでも本文は
 * 素のテキストとして描かれればよく、画面が 2 つの状態を描き分ける必要は無い。
 * @graph-connects none
 */
const EMPTY_CONTEXT: TermLinkContextValue = {
  terms: [],
  termIds: new Set(),
  suppressedTermId: null,
  pjSearch: {},
};

/** 用語辞書の配り先。 @graph-connects none */
const TermLinkContext = createContext<TermLinkContextValue>(EMPTY_CONTEXT);

/** `TermLinkProvider` の props。 */
export interface TermLinkProviderProps {
  /** 重ねる PJ 番号。PJ を選んでいなければ未指定。 */
  pjNumber?: string | undefined;
  /** 中身。 */
  children: ReactNode;
}

/**
 * 用語集 1 件ずつを、本文に当てる辞書の形へ写す。
 *
 * 定義は空文字で置く。作り直しの用語集（`listTerms`)は説明文を返さない — 一覧が返すのは
 * 種別・値のタイプ・属するクラス・参照される数で、説明文は用語詳細（`getTermDetail`）の
 * 持ち物である。全 244 件ぶんの説明文を一覧に載せると、リンクのためだけに一覧の応答が重くなる。
 *
 * @param item 用語集の 1 行
 * @returns 本文に当てる辞書 1 件
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの用語集の 1 行を、本文へ当てる辞書 1 件（番号・表示名・表記）へ写す。
 * @graph-connects none
 */
function toLinkEntry(item: TermSummary): TermLinkEntry {
  return {
    termId: String(item.termId),
    label: item.label,
    definition: '',
    surfaces: { canonical: item.label, all: [item.label], synonyms: [] },
  };
}

/**
 * 用語集から辞書を組む。表示名が空の行と、同じ表示名が 2 件以上ある行は落とす。
 *
 * **表示名が空の行はリンクにできない。** 復元は名前の材料を未設定のまま戻すので、名前を引けない
 * 用語が居る（契約の `label` は「材料が欠けていれば空文字」）。空文字を表記として当てると本文の
 * どこにも当たらないか、当たり方が読めない。
 *
 * **同じ表示名が 2 件以上ある行も落とす。** 番号は表示名から引くしかないので、どちらへ送るかを
 * 決められない。片方へ送れば半分は嘘の行き先になる。
 *
 * @param items 用語集の行
 * @returns 本文に当てる辞書
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語集から、表示名で一意に引ける用語だけの辞書を組む。
 * @graph-connects toLinkEntry [calls] 用語集の 1 行を辞書 1 件へ写す
 */
export function buildTermLinkEntries(items: readonly TermSummary[]): readonly TermLinkEntry[] {
  const countByLabel = new Map<string, number>();
  for (const item of items) {
    if (item.label === '') continue;
    countByLabel.set(item.label, (countByLabel.get(item.label) ?? 0) + 1);
  }

  return items
    .filter((item) => item.label !== '' && countByLabel.get(item.label) === 1)
    .map(toLinkEntry);
}

/**
 * 用語集を取り、配下のすべての本文へ辞書として配る。
 *
 * **辞書の到着を待たせない。** 取得中でも children はそのまま描かれ、本文は素のテキストとして
 * 読める。届いた時点でリンクへ変わる。ここで待たせると、リンクが要らない利用者にまで
 * 白い画面を見せることになる。
 *
 * 案件も一緒に配る。取得を案件で絞るだけでは足りない — **案件で定義した用語へのリンクが
 * `?pj=` を落とすと、押した先がマスターになって「見つかりません」に着く**。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの用語集を 1 回だけ取り、辞書と引き継ぐ案件を配下の本文すべてへ配る。
 * @graph-connects useListTerms [calls] 作り直しの用語集の取得
 * @graph-connects buildTermLinkEntries [calls] 用語集から辞書を組む
 * @graph-connects pjLinkSearch [calls] 案件を次の画面へ引き継ぐ検索条件
 */
export function TermLinkProvider({ pjNumber, children }: TermLinkProviderProps) {
  const dictionary = useListTerms(toElementId(pjNumber));
  const items = dictionary.data?.items;
  const value = useMemo<TermLinkContextValue>(
    () =>
      items === undefined
        ? EMPTY_CONTEXT
        : {
            terms: buildTermLinkEntries(items),
            termIds: new Set(items.map((item) => String(item.termId))),
            suppressedTermId: null,
            pjSearch: pjLinkSearch(pjNumber),
          },
    [items, pjNumber],
  );

  return <TermLinkContext.Provider value={value}>{children}</TermLinkContext.Provider>;
}

/** `TermLinkSuppress` の props。 */
export interface TermLinkSuppressProps {
  /** この用語へのリンクを配下で作らない。 */
  termId: string;
  /** 中身。 */
  children: ReactNode;
}

/**
 * 配下で 1 件の用語だけリンクを作らせない。用語自身のページで使う。
 *
 * 自分の定義文の中に自分へのリンクが並ぶと、押しても同じ場所へ戻るだけの導線が本文へ
 * 何度も挟まる。用語名は定義文の中に必ず出てくるので、抑止しないと必ず起きる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語自身のページで、自分へのリンクだけを作らせない。
 * @graph-connects none
 */
export function TermLinkSuppress({ termId, children }: TermLinkSuppressProps) {
  const outer = useContext(TermLinkContext);
  const value = useMemo<TermLinkContextValue>(
    () => ({
      terms: outer.terms,
      termIds: outer.termIds,
      suppressedTermId: termId,
      pjSearch: outer.pjSearch,
    }),
    [outer.terms, outer.termIds, outer.pjSearch, termId],
  );

  return <TermLinkContext.Provider value={value}>{children}</TermLinkContext.Provider>;
}

/** `ElementTermLink` の props。 */
export interface ElementTermLinkProps {
  /** 参照先の番号。用語でなければリンクにしない。 */
  id: number;
  /** 指すと出す文字（参照先の表示名）。 */
  title: string;
  /** リンクの中身（表示名と未翻訳の印）。 */
  children: ReactNode;
}

/** 用語リンクの見た目。本文の自動リンクと揃える。 @graph-connects none */
const TERM_LINK_CLASS =
  'text-[var(--color-accent-primary)] underline decoration-dotted underline-offset-2';

/**
 * 番号つきの参照が用語を指していれば、用語詳細へのリンクで描く。用語でなければ中身だけを描く。
 *
 * **用語かどうかは番号で引く。** 要素の番号は全型で 1 本の連番なので、用語集に居る番号なら
 * 用語である。表示名で当てる `TermText` と違い、同じ表示名の用語が複数あっても行き先を取り違え
 * ない。
 *
 * **押下を外へ伝えない。** 参照は、押すと選ばれる行や箱（図の業務ルールの箱など）の中にも
 * 置かれる。伝えると、用語へ移る押下が行の選択も起こし、移る直前に右の詳細が切り替わる。
 *
 * `<a>` ・ `<button>` の中では使わない（HTML の入れ子の禁止）。その場所では表示名だけを描く。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 番号つきの参照が用語を指していれば、案件を引き継いだ用語詳細へのリンクとして描く。
 * @graph-connects TERM_DETAIL_ROUTE [references] 用語詳細
 */
export function ElementTermLink({ id, title, children }: ElementTermLinkProps) {
  const { termIds, suppressedTermId, pjSearch } = useContext(TermLinkContext);
  const termId = String(id);
  if (!termIds.has(termId) || termId === suppressedTermId) return <>{children}</>;
  return (
    <Link
      to={TERM_DETAIL_ROUTE}
      params={{ termId }}
      {...pjSearch}
      title={title}
      data-term-ref={termId}
      className={TERM_LINK_CLASS}
      onClick={(event) => {
        event.stopPropagation();
      }}
      onKeyDown={(event) => {
        event.stopPropagation();
      }}
    >
      {children}
    </Link>
  );
}

/** `TermText` の props。 */
export interface TermTextProps {
  /** 描く本文。 */
  text: string;
  /**
   * PJ 差分で変わった語の強調指定。PJ を選んでいない・その本文が書き換えられていないなら
   * 渡さない。
   *
   * 用語リンクと強調は同じ本文の上で重なる。**切り分けは用語リンクが先で、強調はその切れ端の
   * 上に掛かる。** 逆にすると、強調の境目で用語リンクが分断されて 1 つの用語が 2 つのリンクに
   * 割れる。
   */
  highlight?: WordHighlight | null;
  /**
   * この本文が、強調の座標系で何文字目から始まるか。
   *
   * 本文を先に別の規則で割ってから渡す呼び出し（変数参照の切り分け）があり、その切れ端は
   * 本文全体の座標を持たない。渡された基準にこちらの切り分け位置を足して初めて、強調の
   * 区間が正しい場所に掛かる。割らずに渡すなら 0 のままでよい。
   */
  offset?: number;
}

/**
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 本文を用語リンクつきで描き、PJ 差分で変わった語を強調する。
 * @graph-connects TERM_DETAIL_ROUTE [references] 用語詳細
 * @graph-connects HighlightedText [calls] 変わった語の強調
 */
export function TermText({ text, highlight = null, offset = 0 }: TermTextProps) {
  const { t } = useTranslation();
  const { terms, suppressedTermId, pjSearch } = useContext(TermLinkContext);
  /*
   * 切り分けは本文 1 つにつき辞書全件を当てる。一覧の行数ぶん繰り返されるので、同じ本文と
   * 同じ辞書のまま再描画されたときに計算をやり直さない。
   */
  const segments = useMemo(
    () => toTermSegments(text, terms, suppressedTermId),
    [text, terms, suppressedTermId],
  );

  return (
    <>
      {segments.map((segment) =>
        segment.term === null ? (
          <span key={segment.key}>
            <HighlightedText
              text={segment.text}
              offset={offset + segment.offset}
              highlight={highlight}
            />
          </span>
        ) : (
          <Link
            key={segment.key}
            to={TERM_DETAIL_ROUTE}
            params={{ termId: segment.term.termId }}
            {...pjSearch}
            /* 定義を引けない辞書のときは名前だけ出す。「名前:」で終わる吹き出しを出すと、
               定義が空なのか引けなかったのかが読み手に伝わらない。 */
            title={
              segment.term.definition === ''
                ? segment.term.label
                : t('spec.termDefinition', {
                    label: segment.term.label,
                    definition: segment.term.definition,
                  })
            }
            className={TERM_LINK_CLASS}
          >
            <HighlightedText
              text={segment.text}
              offset={offset + segment.offset}
              highlight={highlight}
            />
          </Link>
        ),
      )}
    </>
  );
}
